import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import {
  ListChecks, Plus, Filter, Download, Eye, Pencil, Trash,
  Clock, Warning, CheckCircle, Circle, CaretDown, CaretRight,
  X, Upload, FileText, User, Calendar, Buildings, Target,
  ChartLineUp, ArrowRight, MagnifyingGlass, Funnel, ArrowsClockwise,
  ClipboardText, Shield, Files, ChatCircleText, CaretUp, Export,
  Database, Bank, Globe, Briefcase, FirstAid, Users
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';
import PageBreadcrumb from '@/components/PageBreadcrumb';

// Regulatory theme definitions
const REGULATORY_THEMES = {
  all: { name: 'All Themes', icon: ListChecks, color: '#C9A227' },
  cybersecurity: { name: 'Cybersecurity', icon: Shield, color: '#3B82F6' },
  data_protection: { name: 'Data Protection', icon: Database, color: '#10B981' },
  financial_crime: { name: 'Financial Crime', icon: Bank, color: '#F59E0B' },
  tax_compliance: { name: 'Tax Compliance', icon: Globe, color: '#8B5CF6' },
  corporate_governance: { name: 'Corporate Governance', icon: Briefcase, color: '#EC4899' },
  consumer_protection: { name: 'Consumer Protection', icon: FirstAid, color: '#14B8A6' },
  employment_labour: { name: 'Employment', icon: Users, color: '#F97316' }
};

// Status styles
const STATUS_STYLES = {
  open: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', icon: Circle },
  in_progress: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', icon: ArrowsClockwise },
  pending_review: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', icon: Eye },
  remediated: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', icon: CheckCircle },
  closed: { bg: 'bg-gray-500/10', border: 'border-gray-500/30', text: 'text-gray-400', icon: CheckCircle },
  accepted_risk: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', icon: Shield }
};

// Priority styles
const PRIORITY_STYLES = {
  critical: { bg: 'bg-red-600/10', border: 'border-red-600/30', text: 'text-red-500', label: 'Critical' },
  high: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', label: 'High' },
  medium: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', label: 'Medium' },
  low: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', label: 'Low' }
};

// Stat Card Component
const StatCard = ({ label, value, icon: Icon, color, trend }) => (
  <div className="bg-background-surface border border-border rounded-lg p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs text-text-tertiary uppercase tracking-wider">{label}</span>
      <Icon size={16} className={color} />
    </div>
    <div className="flex items-end gap-2">
      <span className={`text-2xl font-mono font-bold ${color}`}>{value}</span>
      {trend && (
        <span className={`text-xs ${trend > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
  </div>
);

// Create/Edit Modal
const ItemModal = ({ isOpen, onClose, onSave, item, config }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    source_type: 'self_identified',
    source_reference: '',
    priority: 'medium',
    assigned_to: '',
    assigned_department: '',
    due_date: '',
    regulatory_reference: '',
    control_area: '',
    risk_description: '',
    recommended_action: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title || '',
        description: item.description || '',
        source_type: item.source_type || 'self_identified',
        source_reference: item.source_reference || '',
        priority: item.priority || 'medium',
        assigned_to: item.assigned_to || '',
        assigned_department: item.assigned_department || '',
        due_date: item.due_date ? item.due_date.split('T')[0] : '',
        regulatory_reference: item.regulatory_reference || '',
        control_area: item.control_area || '',
        risk_description: item.risk_description || '',
        recommended_action: item.recommended_action || ''
      });
    } else {
      // Calculate default due date (60 days)
      const defaultDue = new Date();
      defaultDue.setDate(defaultDue.getDate() + 60);
      setFormData({
        title: '',
        description: '',
        source_type: 'self_identified',
        source_reference: '',
        priority: 'medium',
        assigned_to: '',
        assigned_department: '',
        due_date: defaultDue.toISOString().split('T')[0],
        regulatory_reference: '',
        control_area: '',
        risk_description: '',
        recommended_action: ''
      });
    }
  }, [item, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background-surface border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="p-4 border-b border-border flex items-center justify-between sticky top-0 bg-background-surface z-10">
          <h3 className="text-lg font-medium text-text-primary">
            {item ? 'Edit Remediation Item' : 'Add Non-Compliant Item'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-background rounded-lg">
            <X size={20} className="text-text-tertiary" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-xs text-text-tertiary uppercase mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
              placeholder="Brief description of the issue"
              required
            />
          </div>

          <div>
            <label className="block text-xs text-text-tertiary uppercase mb-1">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none resize-none"
              rows={3}
              placeholder="Detailed description of the gap or non-compliance"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-text-tertiary uppercase mb-1">Source Type</label>
              <select
                value={formData.source_type}
                onChange={(e) => setFormData({ ...formData, source_type: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
              >
                {config?.source_types && Object.entries(config.source_types).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-text-tertiary uppercase mb-1">Source Reference</label>
              <input
                type="text"
                value={formData.source_reference}
                onChange={(e) => setFormData({ ...formData, source_reference: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
                placeholder="Audit report #, inspection ref, etc."
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-text-tertiary uppercase mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
              >
                {config?.priority_levels && Object.entries(config.priority_levels).map(([key, data]) => (
                  <option key={key} value={key}>{data.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-text-tertiary uppercase mb-1">Due Date</label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-text-tertiary uppercase mb-1">Assigned To</label>
              <input
                type="text"
                value={formData.assigned_to}
                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
                placeholder="Person responsible"
              />
            </div>
            <div>
              <label className="block text-xs text-text-tertiary uppercase mb-1">Department</label>
              <input
                type="text"
                value={formData.assigned_department}
                onChange={(e) => setFormData({ ...formData, assigned_department: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
                placeholder="Department responsible"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-text-tertiary uppercase mb-1">Regulatory Reference</label>
            <input
              type="text"
              value={formData.regulatory_reference}
              onChange={(e) => setFormData({ ...formData, regulatory_reference: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
              placeholder="Act, regulation, or standard reference"
            />
          </div>

          <div>
            <label className="block text-xs text-text-tertiary uppercase mb-1">Control Area</label>
            <input
              type="text"
              value={formData.control_area}
              onChange={(e) => setFormData({ ...formData, control_area: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
              placeholder="e.g., Access Control, Incident Response"
            />
          </div>

          <div>
            <label className="block text-xs text-text-tertiary uppercase mb-1">Risk Description</label>
            <textarea
              value={formData.risk_description}
              onChange={(e) => setFormData({ ...formData, risk_description: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none resize-none"
              rows={2}
              placeholder="Impact and likelihood of the risk"
            />
          </div>

          <div>
            <label className="block text-xs text-text-tertiary uppercase mb-1">Recommended Action</label>
            <textarea
              value={formData.recommended_action}
              onChange={(e) => setFormData({ ...formData, recommended_action: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none resize-none"
              rows={2}
              placeholder="Suggested remediation steps"
            />
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-gold text-gold-foreground rounded-sm text-sm hover:bg-gold-hover disabled:opacity-50"
            >
              {saving ? 'Saving...' : (item ? 'Update Item' : 'Create Item')}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Item Detail Modal
const ItemDetailModal = ({ item, onClose, onUpdate, config }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [newComment, setNewComment] = useState('');
  const [newStatus, setNewStatus] = useState(item?.status || 'open');
  const [statusNotes, setStatusNotes] = useState('');
  const [evidenceDesc, setEvidenceDesc] = useState('');
  const [evidenceType, setEvidenceType] = useState('document');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const statusStyle = STATUS_STYLES[item?.status] || STATUS_STYLES.open;
  const priorityStyle = PRIORITY_STYLES[item?.priority] || PRIORITY_STYLES.medium;
  const StatusIcon = statusStyle.icon;

  const isOverdue = () => {
    if (!item?.due_date || ['remediated', 'closed', 'accepted_risk'].includes(item?.status)) return false;
    return new Date(item.due_date) < new Date();
  };

  const handleStatusUpdate = async () => {
    try {
      const formData = new FormData();
      formData.append('status', newStatus);
      if (statusNotes) formData.append('notes', statusNotes);

      await axios.put(`${API}/remediation-tracker/items/${item.id}/status`, formData);
      toast.success('Status updated');
      setStatusNotes('');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      const formData = new FormData();
      formData.append('comment', newComment);
      await axios.post(`${API}/remediation-tracker/items/${item.id}/comment`, formData);
      toast.success('Comment added');
      setNewComment('');
      onUpdate();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleEvidenceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!evidenceDesc.trim()) {
      toast.error('Please add a description for the evidence');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('evidence_type', evidenceType);
      formData.append('description', evidenceDesc);
      if (file) formData.append('file', file);

      await axios.post(`${API}/remediation-tracker/items/${item.id}/evidence`, formData);
      toast.success('Evidence uploaded');
      setEvidenceDesc('');
      onUpdate();
    } catch (error) {
      toast.error('Failed to upload evidence');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (!item) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background-surface border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="p-4 border-b border-border flex items-start justify-between">
          <div className="flex-1 min-w-0 pr-4">
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-2 py-1 rounded text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                {item.status_label}
              </span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${priorityStyle.bg} ${priorityStyle.text}`}>
                {priorityStyle.label}
              </span>
              {isOverdue() && (
                <span className="px-2 py-1 rounded text-xs font-medium bg-red-600/20 text-red-400 border border-red-600/30">
                  OVERDUE
                </span>
              )}
            </div>
            <h3 className="text-lg font-medium text-text-primary">{item.title}</h3>
            <p className="text-xs text-text-tertiary mt-1">
              {item.source_type_label} • Created {new Date(item.created_at).toLocaleDateString()}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-background rounded-lg">
            <X size={20} className="text-text-tertiary" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border px-4">
          {[
            { id: 'details', label: 'Details', icon: ClipboardText },
            { id: 'evidence', label: 'Evidence', icon: Files },
            { id: 'audit_log', label: 'Audit Log', icon: Clock }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${
                activeTab === tab.id
                  ? 'text-gold border-b-2 border-gold'
                  : 'text-text-tertiary hover:text-text-secondary'
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
              {tab.id === 'evidence' && item.evidence?.length > 0 && (
                <span className="px-1.5 py-0.5 bg-gold/10 text-gold text-xs rounded-full">
                  {item.evidence.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'details' && (
            <div className="space-y-4">
              {/* Description */}
              <div className="p-4 bg-background rounded-lg">
                <h4 className="text-sm font-medium text-text-primary mb-2">Description</h4>
                <p className="text-sm text-text-secondary">{item.description}</p>
              </div>

              {/* Meta Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-background rounded-lg">
                  <div className="text-xs text-text-tertiary mb-1">Assigned To</div>
                  <div className="text-sm text-text-primary flex items-center gap-2">
                    <User size={14} />
                    {item.assigned_to || 'Unassigned'}
                  </div>
                </div>
                <div className="p-3 bg-background rounded-lg">
                  <div className="text-xs text-text-tertiary mb-1">Department</div>
                  <div className="text-sm text-text-primary flex items-center gap-2">
                    <Buildings size={14} />
                    {item.assigned_department || 'Unassigned'}
                  </div>
                </div>
                <div className="p-3 bg-background rounded-lg">
                  <div className="text-xs text-text-tertiary mb-1">Due Date</div>
                  <div className={`text-sm flex items-center gap-2 ${isOverdue() ? 'text-red-400' : 'text-text-primary'}`}>
                    <Calendar size={14} />
                    {item.due_date ? new Date(item.due_date).toLocaleDateString() : 'Not set'}
                  </div>
                </div>
                <div className="p-3 bg-background rounded-lg">
                  <div className="text-xs text-text-tertiary mb-1">Control Area</div>
                  <div className="text-sm text-text-primary flex items-center gap-2">
                    <Shield size={14} />
                    {item.control_area || 'Not specified'}
                  </div>
                </div>
              </div>

              {/* Regulatory Reference */}
              {item.regulatory_reference && (
                <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                  <div className="text-xs text-amber-400 mb-1">Regulatory Reference</div>
                  <div className="text-sm text-text-primary">{item.regulatory_reference}</div>
                </div>
              )}

              {/* Risk Description */}
              {item.risk_description && (
                <div className="p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
                  <div className="text-xs text-red-400 mb-1">Risk Description</div>
                  <div className="text-sm text-text-secondary">{item.risk_description}</div>
                </div>
              )}

              {/* Recommended Action */}
              {item.recommended_action && (
                <div className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                  <div className="text-xs text-emerald-400 mb-1">Recommended Action</div>
                  <div className="text-sm text-text-secondary">{item.recommended_action}</div>
                </div>
              )}

              {/* Status Update */}
              <div className="p-4 bg-background rounded-lg border border-border">
                <h4 className="text-sm font-medium text-text-primary mb-3">Update Status</h4>
                <div className="flex gap-3">
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="flex-1 px-3 py-2 bg-background-surface border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
                  >
                    {config?.status_definitions && Object.entries(config.status_definitions).map(([key, data]) => (
                      <option key={key} value={key}>{data.label}</option>
                    ))}
                  </select>
                </div>
                <input
                  type="text"
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  placeholder="Add notes (optional)"
                  className="w-full mt-2 px-3 py-2 bg-background-surface border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
                />
                <button
                  onClick={handleStatusUpdate}
                  disabled={newStatus === item.status}
                  className="mt-2 px-4 py-2 bg-gold text-gold-foreground rounded-sm text-sm hover:bg-gold-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Update Status
                </button>
              </div>

              {/* Add Comment */}
              <div className="p-4 bg-background rounded-lg border border-border">
                <h4 className="text-sm font-medium text-text-primary mb-3">Add Comment</h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Type your comment..."
                    className="flex-1 px-3 py-2 bg-background-surface border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    className="px-4 py-2 bg-gold text-gold-foreground rounded-sm text-sm hover:bg-gold-hover disabled:opacity-50"
                  >
                    <ChatCircleText size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'evidence' && (
            <div className="space-y-4">
              {/* Upload Evidence */}
              <div className="p-4 bg-background rounded-lg border border-border">
                <h4 className="text-sm font-medium text-text-primary mb-3">Upload Evidence</h4>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <select
                    value={evidenceType}
                    onChange={(e) => setEvidenceType(e.target.value)}
                    className="px-3 py-2 bg-background-surface border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
                  >
                    <option value="document">Document</option>
                    <option value="screenshot">Screenshot</option>
                    <option value="log">System Log</option>
                    <option value="email">Email</option>
                    <option value="policy">Policy Document</option>
                    <option value="procedure">Procedure Document</option>
                    <option value="training_record">Training Record</option>
                    <option value="config">Configuration</option>
                    <option value="other">Other</option>
                  </select>
                  <input
                    type="text"
                    value={evidenceDesc}
                    onChange={(e) => setEvidenceDesc(e.target.value)}
                    placeholder="Description *"
                    className="px-3 py-2 bg-background-surface border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
                  />
                </div>
                <div className="flex gap-2">
                  <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-gold/50 transition-colors">
                    <Upload size={18} className="text-text-tertiary" />
                    <span className="text-sm text-text-tertiary">
                      {uploading ? 'Uploading...' : 'Choose file (optional)'}
                    </span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleEvidenceUpload}
                      disabled={uploading || !evidenceDesc.trim()}
                    />
                  </label>
                  <button
                    onClick={() => handleEvidenceUpload({ target: { files: null } })}
                    disabled={!evidenceDesc.trim() || uploading}
                    className="px-4 py-2 bg-gold text-gold-foreground rounded-sm text-sm hover:bg-gold-hover disabled:opacity-50"
                  >
                    Add Note
                  </button>
                </div>
              </div>

              {/* Evidence List */}
              {item.evidence?.length > 0 ? (
                <div className="space-y-2">
                  {item.evidence.map((ev, i) => (
                    <div key={ev.id || i} className="p-3 bg-background rounded-lg border border-border flex items-start gap-3">
                      <div className="w-8 h-8 rounded bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                        <FileText size={16} className="text-purple-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-text-primary">{ev.description}</div>
                        <div className="text-xs text-text-tertiary mt-1">
                          {ev.evidence_type} • {ev.filename || 'No file'} • {new Date(ev.uploaded_at).toLocaleString()}
                        </div>
                        <div className="text-xs text-text-tertiary">
                          Uploaded by {ev.uploaded_by}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-text-tertiary">
                  <Files size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No evidence uploaded yet</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'audit_log' && (
            <div className="space-y-2">
              {item.audit_log?.length > 0 ? (
                item.audit_log.slice().reverse().map((entry, i) => (
                  <div key={entry.id || i} className="p-3 bg-background rounded-lg border border-border">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-sm text-text-primary capitalize">
                          {entry.action.replace(/_/g, ' ')}
                        </div>
                        {entry.details && (
                          <div className="text-xs text-text-secondary mt-1">{entry.details}</div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-text-tertiary">{entry.actor}</div>
                        <div className="text-xs text-text-tertiary">
                          {new Date(entry.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-text-tertiary">
                  <Clock size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No audit log entries</p>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Main Component
const RemediationTrackerPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [config, setConfig] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(searchParams.get('theme') || 'all');
  const [filters, setFilters] = useState({ status: '', priority: '', source_type: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, [filters, selectedTheme]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      if (selectedTheme !== 'all') {
        params.regulatory_theme = selectedTheme;
      }
      
      const [dashboardRes, itemsRes, configRes] = await Promise.all([
        axios.get(`${API}/remediation-tracker/dashboard`, { params: selectedTheme !== 'all' ? { regulatory_theme: selectedTheme } : {} }),
        axios.get(`${API}/remediation-tracker/items`, { params }),
        axios.get(`${API}/remediation-tracker/config`)
      ]);

      setDashboard(dashboardRes.data);
      setItems(itemsRes.data.items || []);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load remediation data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = async (formData) => {
    try {
      await axios.post(`${API}/remediation-tracker/items`, formData);
      toast.success('Item created successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create item');
      throw error;
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!confirm('Are you sure you want to delete this item?')) return;
    try {
      await axios.delete(`${API}/remediation-tracker/items/${itemId}`);
      toast.success('Item deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleImportFromDiagnostics = async () => {
    try {
      // Get latest assessment
      const assessmentsRes = await axios.get(`${API}/cyber-diagnostics/assessments`);
      const assessments = assessmentsRes.data.assessments || [];
      
      if (assessments.length === 0) {
        toast.error('No diagnostic assessments found');
        return;
      }

      const response = await axios.post(`${API}/remediation-tracker/import-from-diagnostics/${assessments[0].id}`);
      toast.success(`Imported ${response.data.imported_count} items from latest assessment`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import items');
    }
  };

  const filteredItems = items.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.title?.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query) ||
      item.assigned_to?.toLowerCase().includes(query) ||
      item.control_area?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="ml-64 flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
            <span className="text-sm text-text-tertiary">Loading Remediation Tracker...</span>
          </div>
        </div>
      </div>
    );
  }

  const metrics = dashboard?.metrics || {};

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <div className="ml-64 flex-1">
        {/* Breadcrumb */}
        <div className="px-6 pt-4">
          <PageBreadcrumb items={[
            { label: 'Remediation Tracker' }
          ]} />
        </div>

        {/* Header */}
        <div className="px-6 pb-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading font-medium text-text-primary mb-1">
                Remediation Tracker
              </h1>
              <p className="text-sm text-text-secondary">
                Track and manage compliance gaps, audit findings, and remediation activities
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleImportFromDiagnostics}
                className="flex items-center gap-2 px-4 py-2 border border-border text-text-secondary hover:text-gold hover:border-gold/50 rounded-sm text-sm transition-colors"
              >
                <Download size={16} />
                Import from Diagnostics
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gold text-gold-foreground rounded-sm text-sm hover:bg-gold-hover transition-all"
              >
                <Plus size={16} />
                Add Item
              </button>
            </div>
          </div>
        </div>

        {/* Theme Filter Tabs */}
        <div className="p-4 border-b border-border bg-background-surface/50">
          <div className="flex items-center gap-2 overflow-x-auto">
            {Object.entries(REGULATORY_THEMES).map(([key, theme]) => {
              const ThemeIcon = theme.icon;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedTheme(key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-all ${
                    selectedTheme === key 
                      ? 'text-white' 
                      : 'bg-background border border-border text-text-secondary hover:border-gold/50'
                  }`}
                  style={selectedTheme === key ? { backgroundColor: theme.color } : {}}
                >
                  <ThemeIcon size={14} />
                  {theme.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Dashboard Stats */}
        <div className="p-6 border-b border-border">
          <div className="grid grid-cols-6 gap-4">
            <StatCard label="Total Items" value={metrics.total_items || 0} icon={ListChecks} color="text-text-primary" />
            <StatCard label="Open" value={metrics.open_items || 0} icon={Circle} color="text-red-400" />
            <StatCard label="In Progress" value={metrics.in_progress_items || 0} icon={ArrowsClockwise} color="text-amber-400" />
            <StatCard label="Remediated" value={metrics.remediated_items || 0} icon={CheckCircle} color="text-emerald-400" />
            <StatCard label="Overdue" value={metrics.overdue_items || 0} icon={Warning} color="text-red-500" />
            <StatCard label="Remediation Rate" value={`${metrics.remediation_rate || 0}%`} icon={ChartLineUp} color="text-gold" />
          </div>
        </div>

        {/* Filters & Search */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search items..."
                className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm transition-colors ${
                showFilters ? 'border-gold text-gold' : 'border-border text-text-secondary hover:border-gold/50'
              }`}
            >
              <Funnel size={16} />
              Filters
              {(filters.status || filters.priority || filters.source_type) && (
                <span className="w-2 h-2 bg-gold rounded-full" />
              )}
            </button>
          </div>

          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              className="mt-4 flex gap-4"
            >
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
              >
                <option value="">All Statuses</option>
                {config?.status_definitions && Object.entries(config.status_definitions).map(([key, data]) => (
                  <option key={key} value={key}>{data.label}</option>
                ))}
              </select>
              <select
                value={filters.priority}
                onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
              >
                <option value="">All Priorities</option>
                {config?.priority_levels && Object.entries(config.priority_levels).map(([key, data]) => (
                  <option key={key} value={key}>{data.label}</option>
                ))}
              </select>
              <select
                value={filters.source_type}
                onChange={(e) => setFilters({ ...filters, source_type: e.target.value })}
                className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
              >
                <option value="">All Sources</option>
                {config?.source_types && Object.entries(config.source_types).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              <button
                onClick={() => setFilters({ status: '', priority: '', source_type: '' })}
                className="px-3 py-2 text-sm text-text-tertiary hover:text-gold transition-colors"
              >
                Clear
              </button>
            </motion.div>
          )}
        </div>

        {/* Items List */}
        <div className="p-6">
          {filteredItems.length > 0 ? (
            <div className="space-y-3">
              {filteredItems.map((item) => {
                const statusStyle = STATUS_STYLES[item.status] || STATUS_STYLES.open;
                const priorityStyle = PRIORITY_STYLES[item.priority] || PRIORITY_STYLES.medium;
                const StatusIcon = statusStyle.icon;
                const isOverdue = item.due_date && 
                  !['remediated', 'closed', 'accepted_risk'].includes(item.status) &&
                  new Date(item.due_date) < new Date();

                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`bg-background-surface border rounded-lg p-4 hover:border-gold/30 transition-colors cursor-pointer ${
                      isOverdue ? 'border-red-500/50' : 'border-border'
                    }`}
                    onClick={() => setSelectedItem(item)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${statusStyle.bg}`}>
                          <StatusIcon size={18} className={statusStyle.text} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${priorityStyle.bg} ${priorityStyle.text}`}>
                              {priorityStyle.label}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs ${statusStyle.bg} ${statusStyle.text}`}>
                              {item.status_label}
                            </span>
                            {isOverdue && (
                              <span className="px-2 py-0.5 rounded text-xs bg-red-600/20 text-red-400">
                                OVERDUE
                              </span>
                            )}
                            {item.auto_imported && (
                              <span className="px-2 py-0.5 rounded text-xs bg-blue-500/10 text-blue-400">
                                Auto-imported
                              </span>
                            )}
                          </div>
                          <h4 className="text-sm font-medium text-text-primary truncate">{item.title}</h4>
                          <div className="flex items-center gap-4 mt-1 text-xs text-text-tertiary">
                            <span>{item.source_type_label}</span>
                            {item.assigned_to && (
                              <span className="flex items-center gap-1">
                                <User size={10} />
                                {item.assigned_to}
                              </span>
                            )}
                            {item.due_date && (
                              <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-400' : ''}`}>
                                <Calendar size={10} />
                                Due: {new Date(item.due_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id); }}
                          className="p-2 text-text-tertiary hover:text-red-400 transition-colors"
                        >
                          <Trash size={16} />
                        </button>
                        <CaretRight size={16} className="text-text-tertiary" />
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <ListChecks size={48} className="mx-auto text-text-tertiary/30 mb-4" />
              <h3 className="text-lg font-medium text-text-primary mb-2">No Remediation Items</h3>
              <p className="text-text-tertiary mb-4">
                Add items manually or import from your diagnostic assessments
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={handleImportFromDiagnostics}
                  className="flex items-center gap-2 px-4 py-2 border border-border text-text-secondary hover:border-gold/50 rounded-sm text-sm"
                >
                  <Download size={16} />
                  Import from Diagnostics
                </button>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gold text-gold-foreground rounded-sm text-sm hover:bg-gold-hover"
                >
                  <Plus size={16} />
                  Add Manually
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ItemModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleCreateItem}
        item={null}
        config={config}
      />

      {selectedItem && (
        <ItemDetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={() => {
            fetchData();
            // Refresh selected item
            axios.get(`${API}/remediation-tracker/items/${selectedItem.id}`)
              .then(res => setSelectedItem(res.data))
              .catch(() => setSelectedItem(null));
          }}
          config={config}
        />
      )}
    </div>
  );
};

export default RemediationTrackerPage;
